import { create } from 'zustand';

const STORAGE_KEY = 'netbuilder-stock-ui';

export type StockMovementSubTab = 'add_stock' | 'return_stock' | 'transfer' | 'check_out';
export type RequestsViewMode = 'by_item' | 'by_request';

const VALID_STOCK_TABS = new Set([
  'Dashboard', 'Inventory', 'Stock Movement', 'Requests', 'Reports', 'Log',
]);

const VALID_MOVEMENT_TABS = new Set<StockMovementSubTab>([
  'add_stock', 'return_stock', 'transfer', 'check_out',
]);

const VALID_REQUESTS_VIEW = new Set<RequestsViewMode>(['by_item', 'by_request']);

interface PersistedStockUi {
  activeStockTab: string;
  activeMovementSubTab: StockMovementSubTab;
  inventorySubView: 'stock' | 'item_types';
  requestsViewMode: RequestsViewMode;
}

interface StockUiState extends PersistedStockUi {
  setActiveStockTab: (tab: string) => void;
  setActiveMovementSubTab: (tab: StockMovementSubTab) => void;
  setInventorySubView: (view: 'stock' | 'item_types') => void;
  setRequestsViewMode: (mode: RequestsViewMode) => void;
}

function loadPersisted(): Partial<PersistedStockUi> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<PersistedStockUi>;
  } catch {
    return {};
  }
}

function persist(state: PersistedStockUi) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / private mode errors
  }
}

function snapshot(get: () => StockUiState): PersistedStockUi {
  const { activeStockTab, activeMovementSubTab, inventorySubView, requestsViewMode } = get();
  return { activeStockTab, activeMovementSubTab, inventorySubView, requestsViewMode };
}

const saved = loadPersisted();

export const useStockUiStore = create<StockUiState>((set, get) => ({
  activeStockTab:
    saved.activeStockTab && VALID_STOCK_TABS.has(saved.activeStockTab)
      ? saved.activeStockTab
      : 'Dashboard',
  activeMovementSubTab:
    saved.activeMovementSubTab && VALID_MOVEMENT_TABS.has(saved.activeMovementSubTab)
      ? saved.activeMovementSubTab
      : 'add_stock',
  inventorySubView: saved.inventorySubView === 'item_types' ? 'item_types' : 'stock',
  requestsViewMode:
    saved.requestsViewMode && VALID_REQUESTS_VIEW.has(saved.requestsViewMode)
      ? saved.requestsViewMode
      : 'by_item',

  setActiveStockTab: (tab) => {
    set({ activeStockTab: tab });
    persist(snapshot(get));
  },

  setActiveMovementSubTab: (tab) => {
    set({ activeMovementSubTab: tab });
    persist(snapshot(get));
  },

  setInventorySubView: (view) => {
    set({ inventorySubView: view });
    persist(snapshot(get));
  },

  setRequestsViewMode: (mode) => {
    set({ requestsViewMode: mode });
    persist(snapshot(get));
  },
}));
